// @givmo/board — "Record something from before", on the import page: one document, no batch
// (CBG-UX11, D-CBG-39; design-284 `scratchpad/2026-10-24-import-without-batches-design/` §2.1, §2.2,
// frames i1–i3).
//
// ══ WHAT THIS FORM IS ═══════════════════════════════════════════════════════════════════════════
//
// RC8's by-hand form (`imported-record-dialog.tsx`), taken out of its dialog and its batch and put on
// the page. It posts to the record core's ONE import door, `POST …/imported-records`, with no
// `import_batch_id`: an imported record lands immutable on entry and never goes through submit or
// approve, so what this form sends is what the minute book will say.
//
// The fields come in the order a person holding a document thinks about them (design §2.1): what it
// is and its date side by side, what its kind needs, the title (the one required field), the file,
// where it came from (optional on every record, Jonathan 2026-10-24 14:28 PDT), what it says
// (optional: the file holds it), the links, the closed-session switch, "Record it". The date starts
// empty. No Cancel: nothing uploads until "Record it". The pickers whose answer is "No" or "Not
// recorded" show that answer, never a blank "Select…".
//
// THE FILE GOES IN WITH THE RECORD, through a slot that names no batch (design §4 B2): on "Record it"
// the page asks for the slot, PUTs the file, and sends its upload id with the record; the door checks
// the file and copies it to the record's own key before the record is entered. After one of the
// record's own refusals a retry sends the same upload; after the file's own refusal, an upload already
// used, or an answer that the record is already in the book (Board then clears the upload away), the
// next attempt uploads afresh. Removing a chosen file after an upload asks Board to delete it
// (best-effort; Board's age sweep catches what this misses).
//
// ⚠ A REPLAY IS NOT AN ERROR (design §4 B4). The door de-duplicates the organization's batchless
// imports by content address and answers 200 with the record already there, so a retried "Record it"
// never puts a document in the book twice. The form then keeps what was typed, so a person who meant
// a second, different document can say which is which in "Where this came from" and record it again.
// A duplicate the person may not read (a closed session's) is refused with the same sentence
// (`already_recorded`), and reads the same way.
//
// ⚠ A CLOSED SESSION IS OFFERED WITH NO PARTICIPANTS, DELIBERATELY (RC8's reason): a roster typed in
// years later would be a false record of presence. Whoever enters it is the one it is sent to, and
// every current director may open it, each opening recorded in its history (D-CBG-25 as amended);
// the form says so before the officer chooses (CBG-UX-COPY5's words, D-CBG-52).

import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  firstErrorCode,
  isGivmoError,
  type BoardProposalFileContentType,
  type OrgBoardGoverningDocumentOut,
  type OrgBoardRecordOut,
  type OrgBoardRecordReadOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Field,
  Icon,
  Select,
  Switch,
  Textarea,
} from '@givmo/design-system';
import { FileText } from 'lucide-react';
import { useBoardOperations } from '../../board-runtime';
import { displayDate } from '../../lib/display';
import {
  DOCUMENT_KINDS,
  chainVersions,
  extractGoverningDocuments,
  requiresLabel,
} from '../govdocs/govdocs-decode';
import {
  FILE_ACCEPT,
  MAX_FILE_BYTES,
  chosenFileType,
  fileCheckRefusal,
  putToSlot,
} from '../proposals/proposal-decode';
import { fileKindWord, proposalWords as pw } from '../proposals/proposal-words';
import { isTheRecord, recordDateWording, recordKindLabel } from '../records/records-decode';
import { fileSize } from '../returns/return-decode';
import { IMPORTABLE_RECORD_KINDS } from './import-decode';
import { importWords as iw } from './import-words';

/** The record kinds the backend accepts as the act that adopted another record. */
const ADOPTING_RECORD_KINDS: readonly string[] = ['minutes', 'resolution', 'written_consent'];

/** A picker's "No" / "None chosen" answer: a value of its own, so the picker shows the words. */
const NONE = 'none';

/** The import door's refusals that leave no upload to reuse: Board's check of the file deleted it,
 *  a committed record already names it, or the record is already in the book (design §4 B4). */
const UPLOAD_SPENT = new Set([
  'wrong_type',
  'too_large',
  'unreadable',
  'upload_already_used',
  'already_recorded',
]);

const HELPER = 'mt-6 font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';
/** The file's label, spaced as frame i1 draws it above its button. */
const FILE_LABEL = 'mb-8 font-sans text-s13 font-w500 tracking-givmo text-text-secondary';
/** A note set flush under the control it explains (the file's button, the version picker), as
 *  frames i1 and i2 draw them. */
const NOTE_UNDER = 'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';

/** A refusal this form words itself (the file's kind or size, a PUT storage refused). */
class SaidError extends Error {}

/** An upload through the batchless slot, kept for a retry of the same file. */
interface Uploaded {
  file: File;
  uploadId: string;
  contentType: BoardProposalFileContentType;
}

export interface ImportOneFormProps {
  orgRef: string;
  /** A record went in, or the door answered with the one already there (`already_recorded`). */
  onRecorded: (record: OrgBoardRecordReadOut) => void;
  /** The door refused a duplicate this person may not read: the book already holds it. */
  onAlreadyInTheBook: () => void;
}

export function ImportOneForm({ orgRef, onRecorded, onAlreadyInTheBook }: ImportOneFormProps) {
  const operations = useBoardOperations();
  const [recordKind, setRecordKind] = useState('minutes');
  const [occurredOn, setOccurredOn] = useState('');
  const [documentKind, setDocumentKind] = useState('bylaws');
  const [documentLabel, setDocumentLabel] = useState('');
  const [supersedesRecordId, setSupersedesRecordId] = useState(NONE);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState<Uploaded | null>(null);
  const [sendingFile, setSendingFile] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [sourceNote, setSourceNote] = useState('');
  const [body, setBody] = useState('');
  const [meetingRecordId, setMeetingRecordId] = useState(NONE);
  const [adoptedByRecordId, setAdoptedByRecordId] = useState(NONE);
  const [closedSession, setClosedSession] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isGoverningDocument = recordKind === 'governing_document';
  const needsLabel = requiresLabel(documentKind);
  const supersedes = supersedesRecordId === NONE ? '' : supersedesRecordId;
  const meetingRecord = meetingRecordId === NONE ? '' : meetingRecordId;
  const adoptedBy = adoptedByRecordId === NONE ? '' : adoptedByRecordId;

  // The 100 most recent records this reader may see, newest first. A backfill names a meeting or an
  // adopting act it entered MOMENTS ago, so the recent window is the picker's real population.
  const recordsQuery = useQuery({
    queryKey: ['board-records', orgRef, 'linkable'],
    queryFn: async (): Promise<OrgBoardRecordOut[]> => {
      const { data } = await operations.officerListBoardRecords({
        path: { org_ref: orgRef },
        query: { 'page[limit]': 100 },
        throwOnError: true,
      });
      const rows = Array.isArray(data)
        ? data
        : ((data as { data?: OrgBoardRecordOut[] })?.data ?? []);
      return rows.filter(isTheRecord);
    },
  });

  const registryQuery = useQuery({
    queryKey: ['board-governing-documents', orgRef],
    enabled: isGoverningDocument,
    queryFn: async (): Promise<OrgBoardGoverningDocumentOut[]> => {
      const { data } = await operations.officerListBoardGoverningDocuments({
        path: { org_ref: orgRef },
        throwOnError: true,
      });
      return extractGoverningDocuments(data);
    },
  });

  const meetings = (recordsQuery.data ?? []).filter((record) => record.record_kind === 'meeting');
  const adoptingRecords = (recordsQuery.data ?? []).filter((record) =>
    ADOPTING_RECORD_KINDS.includes(record.record_kind),
  );

  // Every version already recorded for the instrument being entered, in the order the backend walked
  // them — so an officer working oldest-first can name the one this replaces.
  const predecessors = useMemo(() => {
    const document = (registryQuery.data ?? []).find(
      (entry) =>
        entry.document_kind === documentKind &&
        (needsLabel ? (entry.label ?? '').trim() === documentLabel.trim() : true),
    );
    return document === undefined ? [] : chainVersions(document);
  }, [registryQuery.data, documentKind, documentLabel, needsLabel]);

  /** An upload no attempt will name again: Board is asked to delete it, best-effort. */
  const dropUpload = () => {
    if (uploaded === null) return;
    void operations
      .officerDeleteBoardImportRecordFileUpload({
        path: { org_ref: orgRef, upload_id: uploaded.uploadId },
      })
      .catch(() => undefined);
    setUploaded(null);
  };

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] ?? null;
    // Cleared, so choosing the SAME file again still fires a change event.
    event.target.value = '';
    if (chosen === null) return;
    // Before anything uploads (design §3.3): a person's slot carries no size range.
    if (chosenFileType(chosen) === null) {
      setError(pw.addFileKinds);
      return;
    }
    if (chosen.size > MAX_FILE_BYTES) {
      setError(pw.chosenTooLarge);
      return;
    }
    setError(null);
    dropUpload();
    setFile(chosen);
  };

  /** Empty again for the next document, the kind kept (a person usually enters a run of one kind). */
  const reset = () => {
    setOccurredOn('');
    setDocumentLabel('');
    setSupersedesRecordId(NONE);
    setTitle('');
    setFile(null);
    setUploaded(null);
    setSourceNote('');
    setBody('');
    setMeetingRecordId(NONE);
    setAdoptedByRecordId(NONE);
    setClosedSession(false);
    setError(null);
  };

  /** The file's part of the body: uploaded first (slot, then PUT), or the kept upload reused. */
  const fileForTheRecord = async () => {
    if (file === null) return null;
    let upload = uploaded !== null && uploaded.file === file ? uploaded : null;
    if (upload === null) {
      const contentType = chosenFileType(file);
      if (contentType === null) throw new SaidError(pw.addFileKinds);
      setSendingFile(true);
      try {
        const issued = await operations.officerIssueBoardImportRecordFileUploadUrl({
          path: { org_ref: orgRef },
          body: { file_name: file.name, content_type: contentType },
          throwOnError: true,
        });
        if (file.size > issued.data.max_byte_size) throw new SaidError(pw.chosenTooLarge);
        const uploadId = issued.data.upload_id ?? null;
        if (uploadId === null || !(await putToSlot(issued.data, file))) {
          throw new SaidError(pw.fileDidNotArrive);
        }
        upload = { file, uploadId, contentType };
        setUploaded(upload);
      } finally {
        setSendingFile(false);
      }
    }
    return { upload_id: upload.uploadId, file_name: file.name, content_type: upload.contentType };
  };

  const record = useMutation({
    mutationFn: async (): Promise<OrgBoardRecordReadOut> => {
      const fileBody = await fileForTheRecord();
      const note = sourceNote.trim();
      const { data } = await operations.officerImportBoardRecord({
        path: { org_ref: orgRef },
        body: {
          record_kind: recordKind as 'minutes',
          title: title.trim(),
          body,
          confidentiality: closedSession ? 'executive_session' : 'standard',
          // Optional on every record (design §4 B9): left out when blank.
          ...(note.length > 0 ? { source_note: note } : {}),
          ...(occurredOn.length > 0 ? { occurred_on: occurredOn } : {}),
          ...(fileBody !== null ? { file: fileBody } : {}),
          ...(meetingRecord.length > 0 && recordKind !== 'meeting'
            ? { meeting_record_id: meetingRecord }
            : {}),
          // The adopting pointer arrives EITHER at the top level OR inside the descriptor, never
          // both — the backend answers 422 for the pair, and both paths validate the same way.
          ...(adoptedBy.length > 0 && recordKind === 'resolution'
            ? { adopted_by_record_id: adoptedBy }
            : {}),
          ...(isGoverningDocument
            ? {
                governing_document: {
                  document_kind: documentKind as 'bylaws',
                  ...(needsLabel ? { label: documentLabel.trim() } : {}),
                  ...(supersedes.length > 0 ? { supersedes_record_id: supersedes } : {}),
                  ...(adoptedBy.length > 0 ? { adopted_by_record_id: adoptedBy } : {}),
                },
              }
            : {}),
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (entered) => {
      if (entered.already_recorded === true) {
        // Board cleared this attempt's upload away; what was typed stays for a second document.
        setUploaded(null);
      } else {
        reset();
      }
      onRecorded(entered);
    },
    // Rendered VERBATIM — the backend's refusals here already say what to do ("a document's versions
    // are recorded oldest first").
    onError: (caught) => {
      if (caught instanceof SaidError) {
        setError(caught.message);
        return;
      }
      const code = isGivmoError(caught) ? (firstErrorCode(caught) ?? '') : '';
      // The file's own refusal, an upload already used, or a duplicate: the next attempt uploads
      // afresh. The record's own refusals keep the upload for a retry.
      if (UPLOAD_SPENT.has(code)) setUploaded(null);
      if (code === 'already_recorded') {
        onAlreadyInTheBook();
        return;
      }
      setError(
        fileCheckRefusal(caught, file !== null ? chosenFileType(file) : null) ??
          (isGivmoError(caught) ? caught.message : 'We couldn’t record that. Please try again.'),
      );
    },
  });

  const canSave =
    title.trim().length > 0 &&
    (!isGoverningDocument || !needsLabel || documentLabel.trim().length > 0) &&
    !record.isPending;

  return (
    <Card testId="import-one-form">
      <CardBody className="flex flex-col gap-16">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
            Record something from before
          </h2>
          <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {iw.formLead}
          </p>
        </div>

        {/* What it is, and the day it is kept from, side by side: the label follows the kind. */}
        <div className="grid grid-cols-1 gap-16 sm:grid-cols-[minmax(0,1fr)_200px]">
          <Select
            label="What is it"
            appearance="saas"
            value={recordKind}
            onValueChange={(next) => next !== null && setRecordKind(next)}
            options={IMPORTABLE_RECORD_KINDS.map((entry) => ({
              value: entry.value,
              label: entry.label,
            }))}
            testId="import-record-kind"
          />
          <Field
            label={recordDateWording(recordKind).label}
            type="date"
            value={occurredOn}
            onChange={(event) => setOccurredOn(event.target.value)}
            testId="import-occurred-on"
          />
        </div>

        {isGoverningDocument ? (
          <>
            <Select
              // Approved (CBG-UX-COPY3's I3): the page's own word for what is held.
              label="Which document"
              appearance="saas"
              value={documentKind}
              onValueChange={(next) => {
                if (next === null) return;
                setDocumentKind(next);
                setSupersedesRecordId(NONE);
              }}
              options={DOCUMENT_KINDS.map((entry) => ({ value: entry.value, label: entry.label }))}
              testId="import-document-kind"
            />
            {needsLabel ? (
              <Field
                label="Which one"
                value={documentLabel}
                maxLength={200}
                onChange={(event) => setDocumentLabel(event.target.value)}
                testId="import-document-label"
              />
            ) : null}
            <div className="flex flex-col">
              <Select
                label="Does this replace a version you have already recorded?"
                appearance="saas"
                value={supersedesRecordId}
                onValueChange={(next) => setSupersedesRecordId(next ?? NONE)}
                options={[
                  { value: NONE, label: iw.replacesNothing },
                  ...predecessors.map((version) => ({
                    value: version.record_id,
                    label: `Replaces ${(version.record?.title ?? '').trim() || 'an earlier version'}${
                      version.adopted_on ? ` (${displayDate(version.adopted_on)})` : ''
                    }`,
                  })),
                ]}
                testId="import-document-supersedes"
              />
              <p className={NOTE_UNDER} data-testid="import-oldest-first">
                Record a document’s versions oldest first — each one names the version it replaces,
                so the earlier one has to be in your minute book already.
              </p>
            </div>
          </>
        ) : null}

        <Field
          label="Title"
          value={title}
          maxLength={300}
          onChange={(event) => setTitle(event.target.value)}
          testId="import-title"
        />

        <div className="flex flex-col" data-testid="import-file">
          <span className={FILE_LABEL}>{pw.byHandFileLabel}</span>
          <input
            ref={fileInput}
            type="file"
            accept={FILE_ACCEPT}
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={chooseFile}
            data-testid="import-file-input"
          />
          {file !== null ? (
            <div
              className="flex items-center gap-10 rounded-lg border border-border-subtle px-12 py-10"
              data-testid="import-file-chosen"
            >
              <Icon icon={FileText} size={18} aria-hidden className="shrink-0 text-text-tertiary" />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong">
                  {file.name}
                </span>
                <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                  {`${fileKindWord(chosenFileType(file))} · ${fileSize(file.size)}`}
                </span>
              </span>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={record.isPending}
                onClick={() => {
                  dropUpload();
                  setFile(null);
                }}
                testId="import-file-remove"
              >
                {pw.remove}
              </Button>
            </div>
          ) : (
            <div>
              <Button
                appearance="saas"
                tone="secondary"
                disabled={record.isPending}
                onClick={() => fileInput.current?.click()}
                testId="import-file-add"
              >
                {pw.addTheFile}
              </Button>
            </div>
          )}
          <p className={NOTE_UNDER} data-testid="import-file-note">
            {pw.byHandFileNote}
          </p>
        </div>

        <Field
          label={iw.sourceNoteLabel}
          value={sourceNote}
          maxLength={2000}
          helper={iw.sourceNoteHelper}
          onChange={(event) => setSourceNote(event.target.value)}
          testId="import-source-note"
        />

        <Textarea
          label={iw.bodyLabel}
          helper={iw.bodyHelper}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          testId="import-body"
        />

        {meetings.length > 0 && recordKind !== 'meeting' ? (
          <Select
            label="The meeting this is about (optional)"
            appearance="saas"
            value={meetingRecordId}
            onValueChange={(next) => setMeetingRecordId(next ?? NONE)}
            options={[
              { value: NONE, label: iw.noneChosen },
              ...meetings.map((entry) => ({ value: entry.id, label: entry.title })),
            ]}
            testId="import-meeting-link"
          />
        ) : null}

        {adoptingRecords.length > 0 && (isGoverningDocument || recordKind === 'resolution') ? (
          <Select
            label="The record of the act that adopted it (optional)"
            appearance="saas"
            value={adoptedByRecordId}
            onValueChange={(next) => setAdoptedByRecordId(next ?? NONE)}
            options={[
              { value: NONE, label: iw.noneChosen },
              ...adoptingRecords.map((entry) => ({
                value: entry.id,
                label: `${recordKindLabel(entry.record_kind)} — ${entry.title}`,
              })),
            ]}
            testId="import-adopted-by"
          />
        ) : null}

        <div className="flex flex-col gap-8">
          <label className="flex items-center gap-10">
            <Switch
              appearance="saas"
              checked={closedSession}
              onCheckedChange={setClosedSession}
              testId="import-closed-session"
            />
            <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
              This was a closed session
            </span>
          </label>
          {closedSession ? (
            <p className={HELPER} data-testid="import-closed-session-note">
              Givmo can’t say who was in a room it wasn’t in, so it won’t record one. Any current
              director can open this record, and its openings are recorded in its history.
            </p>
          ) : null}
        </div>

        {error !== null ? <ErrorMessage testId="import-record-error">{error}</ErrorMessage> : null}

        <div>
          <Button
            appearance="saas"
            tone="primary"
            loading={record.isPending}
            disabled={!canSave}
            onClick={() => {
              setError(null);
              record.mutate();
            }}
            testId="import-record-save"
          >
            {sendingFile ? pw.addingTheFile : 'Record it'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
